/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name align max-line-length */

import * as $ from 'jquery';

import {ProjectElement} from './project.element';
import {ProjectElementType} from './project.element.type';
import {Project} from '../project';

import {SimulationJob, SimulationStatusType} from '@/models/sim/simulation.job';
import {SimpleSimJob} from '@/models/sim/simple.sim.job';

import {BlobType} from '../blob';

import {Logger} from '@/util/logger';

import {KPIElement} from '@/models/project/k.p.i.element';
import {ProjectElementFactory} from '@/models/project.element.factory';

import {empty, errorAsText, isset} from '@/util/util';
import {StringLineViewerMetadata} from '@/screens/ide/string.line.viewer.metadata';
import {UserLogViewerMetadata} from '@/screens/ide/user.log.viewer.metadata';
import {JSONBrowserMetadata} from '@/screens/ide/json.browser.metadata';
import {TextEditorMetadata} from '@/screens/ide/text.editor.metadata';

import {catchError, first, tap} from 'rxjs/operators';
import {forkJoin, Observable, of} from 'rxjs';
import {ProjectElementFactoryDecorator} from "@/models/project/project.element.factory.decorator";
import {PropertiesHolder} from "@/models/properties.holder";
import {TaskUpdate} from "@/models/task.update";
import {TaskSimpleSim} from "@/models/task.simple.sim";
import {TaskSimpleSimComplete} from "@/models/task.simple.sim.complete";
import {TaskSimpleSimFailed} from "@/models/task.simple.sim.failed";
import {FileBrowserMetadata} from "@/screens/ide/file.browser.metadata";
import {KPIXElement} from "@/models/project/k.p.i.x.element";
import {PropertyElement} from "@/models/project/property.element";

/**
 *
 * @class LineSimJobElement
 *
 * @brief node to maps directly to a result of doing a remote line simulator job
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */
@ProjectElementFactoryDecorator('LineSimJobElement')
export class LineSimJobElement extends ProjectElement {

  /**
   * updates normally happen about every 1 second, so if just modulo divide by 5
   * we know to save the project every 5 updates...so we can keep the project
   * reasonably up to date when things are changing a lot...but still not be
   * constantly saving the project.
   *
   */
  private updateCount = 0;

  /** don't allow double updating */
  private downloading: boolean = false;

  /** the unique progress task we're showing in the long-running tasks popup */
  private taskId: string = '';

  protected actionKeys: string[] = [
    'viewcrash',
    'viewstringline',
    'downloadlog',
    'editlog',
    'downloadoutput',
    'viewoutput',
    'renameresult',
    'deleteresult',
    'generatekpi',
    'generatekpitable',
    'userlog',
    'userlogplus',
    'userlognew',
    'viewinbrowser'
  ];

  /* all the details to track/manage the remote line simulator job */
  public _job: SimpleSimJob = null;

  /** standard constructor */
  constructor(
     project: Project,
          id: string,
         job: SimpleSimJob,
      parent: ProjectElement,
    publish?: boolean,
     peType?: ProjectElementType
  ) {

    super(project, id, peType ? peType : ProjectElementType.LineSimResult);

    this._job         = job;
    this._enabled     = true;
    this._label       = `[0%] #- : `;
    this._icon        = '';
    this._elementKind = 'LineSimResult';

    if (this._job) {

      this._label     = '[' + job.progress.toFixed(0) + `%] #${job.id()} ${SimulationJob.statusString(this._job.status)}: ` + job.updated.toISOString();

      this._icon      = job.icon();

      if (this._job.active()) {

        this._project.registerJob(this._job, this);
      }

      if (this._job.crashed()) {

        this._label = `#${this._job.id()} crashed: ` + this._job.updated.toString();
      }
    }

    if (parent) {

      this._project.addElement(this, parent, publish);
    }

  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType() {
    console.log("requiring LineSimJobElement");
  }

  /** drop data on this element */
  public acceptDrop(data): boolean {

    return false;
  }

  /** all are notified (this method) after they are hooked into the project tree */
  public onAdded(): void {

    this.updateParentIcon();
  }

  /** helper to ensure the parent scenario is colored appropriately */
  public updateParentIcon(): void {

    if (!this.parent() || (this.parent().blobType() != BlobType.Scenario)) {

      /* parent isn't a scenario */

      return ;
    }

    let icon: string = 'blob-scenario-20.png';

    if (this._job.failed() || this._job.crashed()) {

      /* failed somehow */

      icon = 'blob-scenario-bad-20.png';

    } else if (this._job.complete()) {

      /* completed ok! */

      icon = 'blob-scenario-good-20.png';

    } else if (this._job.active()) {

      /* it's in progress */

      icon = 'blob-scenario-progress-20.png';

    }

    /* tell the scenario parent to use a more indicative icon ... */

    this.parent().updateIcon(icon);

  }

  /** is this element deletable? */
  public isDeletable() : boolean {

    const kids: ProjectElement[] = this.children();

    return kids.length <= 0;
  }

  /** test if any blob of this type can be a child of this element */
  public acceptsBlobType(type: BlobType): boolean {

    return false;
  }

  /** convert this element to a simplified form that can be easily serialized */
  public node(init?: any) {

    return super.node({job: this._job.toJSON()});
  }

  /** initialize this element given the data in the simplified node object */
  public fromNode(node: any): void {

    /* recover the basics */

    super.fromNode(node);

    /* recover the job details */

    this._job  = new SimpleSimJob();
    this._job.fromJSON(node.job);

    this._project.registerJob(this._job, this);

    /* if the job is running when we saved last, try to recover the status as it is now */

    if (this._job.active()) {

      if (this.hasLog() && this.hasOutput()) {

        this._label = `#${this._job.id()} ${SimulationJob.statusString(this._job.status)}: ` + this._job.updated.toISOString();

      } else if (this._job.failed()) {

        this._label = `#${this._job.id()} ${SimulationJob.statusString(this._job.status)}: ` + this._job.updated.toISOString();

      } else {

        this._label = '[' + this._job.progress.toFixed(0) + `%] #${this._job.id()}  ${SimulationJob.statusString(this._job.status)}:` + this._job.updated.toISOString();
      }

      if (this._job.crashed()) {

        this._label = `#${this._job.id()} crashed: ` + this._job.updated.toISOString();
      }
    }

    /* icon is set per the job's known status */

    this._icon      = this._job.icon();

    /* ensure the parent scenario icons is colored appropriately */

    this.updateParentIcon();
  }

  /* show proportional progress right in the node */
  updateProgressBar(): void {

    /* try to find the title <span> It's where the label is shown */

    const        id: string = this._id;
    const titleSpan: JQuery = $(`li#${id} span.fancytree-title`);

    if ( !$(titleSpan).is('span')) {

      /* can't find this node */

      return ;
    }

    /*
     * try to find the progress <div> that we insert inside the title to show as progress,
     * behind the label text.
     *
     */

    let progressBar: JQuery = $(`li#${id} span.fancytree-title div`);

    if ( !this._job.active()) {

      /* not tracking anymore */

      if ( $(progressBar).is('div')) {

        /* clear out the progress bar */

        $(progressBar).remove();
      }

      return ;
    }

    if ( !$(progressBar).is('div')) {

      /* first add of the progress */

      $(titleSpan).append('<div></div>');

      progressBar = $(`li#${id} span.fancytree-title div`);
    }

    /*
     * fetch the width of the title span, so that we know what width to use for
     * the progress bar.
     */

    const width:   number = $(titleSpan).width();
    const percent: number = this._job.progress / 100.0;
    const w:       number = Math.floor(width * percent);

    $(progressBar).height(20);
    $(progressBar).width(w);

    $(progressBar).css('position',         'absolute');
    $(progressBar).css('top',              0);
    $(progressBar).css('z-index',          -1);
    $(progressBar).css('background-color', '#2a8c2e');

  }

  /** helper to update the long-running tasks panel */
  taskUpdate(message: string) {

    if (empty(this.taskId)) {

      /*
       * we're updating, so clearly in the middle of the job, could be either because we just
       * started the job, or the page was reloaded, and we are re-establishing. Either way,
       * allocate a long-running task id, so we can track progress more visibly in the long
       * running tasks pop up panel.
       *
       */

      this.taskId =  TaskUpdate.generateId();
    }

    let progress: number  = 0.0;
    let subStatus: string = '';

    if (this._job) {

      progress      = this._job.progress;
      let jobStatus = SimulationJob.statusString(this._job.status);

      if (this._job.status == SimulationStatusType.NONE) {

        jobStatus   = 'pending';
      }

      subStatus = `#${this._job.id()} ${progress.toFixed(2)}% ${jobStatus} `;

      if (this._job.crashed()) {

        subStatus = `#${this._job.id()} crashed - `;
      }
    }

    let icon   = '/assets/images/cloud-run-icon-32.png';

    /* if it's not cloud sim, don't use the cloud icon, us the stand-alone icon */

    if ( ! /cloudsim/.test(this._job.config.endpoint)) {

      icon = '/assets/images/line-sim-icon-72.png';
    }

    let update = new TaskSimpleSim(this.taskId, `${subStatus} ${message}`, progress, icon);

    Logger.debug(`[LineSimJobElement] progress: ${this._id} `, update);

    this._project.services.taskMonitor.progress(update);
  }

  /* general refreshing when the job progress has changed */
  update(): void {

    this.updateCount++;

    /* don't redraw while we are downloading...just redraw once we have the artifacts in hand */

    if (this.downloading) {

      return ;
    }

    const flags = ['-', '\\', '|', '/'];
    let prefix = 'simulating';

    /* update long running task progress... */

    if ((this._job.status == SimulationStatusType.FAIL)||(this._job.status == SimulationStatusType.SUCCESS)) {

      if (!this._job.resolved) {

        /* its already completed or failed, but we're waiting for artifact download from Google to cloud sim */

        prefix = '(remote) downloading';

      } else {

        prefix = 'downloading';
      }
    }

    this.taskUpdate(`${flags[this.updateCount % 4]} ${prefix}, please wait ...`);

    /* if we have the outputs, we're good! */

    if (this.hasLog() && this.hasOutput()) {

      this._parent.unBusy();

      /* nothing to do */

      this._label     = `#${this._job.id()} ${SimulationJob.statusString(this._job.status)}: ` + this._job.updated.toISOString();

      this._icon      = this._job.icon();

      this.redraw();

      /* ensure the parent scenario icons is colored appropriately */

      this.updateParentIcon();

      return ;
    }

    const oldLabel = this._label;

    this._label     = '[' + this._job.progress.toFixed(0) + `%] #${this._job.id()} ${SimulationJob.statusString(this._job.status)}: ` + this._job.updated.toISOString();

    /*
     * during the job's normal work phase, don't do any extra updates (kills performance).  Once we are
     * resolved though, we do all updates to ensure we catch the downloads.
     *
     */

    if (!this._job.resolved) {

      if (oldLabel === this._label) {

        /*
         * if the update didn't actually change anything, then we don't do the rest of the update, because it
         * will trigger saving of the project...which will spam the serve with project save requests unnecessarily.
         *
         */

        return;
      }

    }

    this._icon      = this._job.icon();

    /* ensure the parent scenario icons is colored appropriately */

    this.updateParentIcon();

    this.redraw();

    /* try to show progress bar as background color on the node ... */

    setTimeout( () => {

      this.updateProgressBar();

    }, 0);

    if ( !this._job.active()) {

      /* time to stop watching */

      this._project.unregisterJob(this._job, this);

      /*
       * download the log and output, when its cached locally, we have everything we need
       * to do local debugging and string viewing.
       *
       */

      this.downloadResults();

    } else {

      /* trigger a project save roughly every 5 seconds */

      /*
       * if we're only saving for the sake of saving the intermediate progress while
       * the sim is running...we don't really have to...if the page is reloaded, the status
       * will be checked and updated anyway, and when we checkpoint the project when
       * the simulation(s) complete and do their results download etc...that will be a
       * better save point anyway.
       *
       * the issue is that if you launched 30 simulations, you'll be doing project
       * saves every 5 updates for all 30 jobs, so every couple of seconds its trying to
       * do 150 project saves!  That creates a lot of stress on the system, and more chances
       * for things to go wrong.
       *
      if ((LineSimJobElement.updateCount % 5) === 0) {

        this._project.save();
      }
      *
      *
      */
    }
  }

  /** fetch the results from the line simulator and cache them here inside the project...in this leaf. */
  public downloadResults() {

    this.updateProgressBar();

    this.downloading = true;

    Logger.debug('[result] downloading...', this._job);

    this.taskUpdate(`downloading (logs, outputs), please wait ...`);

    /*
     * for the various artifacts we might get from a simulation run in CloudRun,
     * save the outputs in:
     *
     *    /home/users/<user>/projects/<project>/results/<uuid of result>
     *
     * we can get back:
     *
     *    log file    - stdout of linesim
     *    error file  - the set of validation errors in scenario
     *    cmd file    - the input configuration given to the line simulator
     *    output file - the train movement (simulation output, JSON)
     *    kpi file    - the JSON formatted key performance indicators, summary metrics
     *    kpix file   - extended version of KPI
     *
     * Note: forkJoin() will fail for everything, if any single request fails, so we have to
     * intercept our download request responses, and when they fail, provide a fake result
     * so we can still get partial download of whatever artifacts we have.
     *
     * This typically happens when the simulator crashes hard, and dosn't generate some of its
     * artifacts...because it crashed!
     *
     */

    this._label     = `[downloading...] #${this._job.id()} ${SimulationJob.statusString(this._job.status)}: ` + this._job.updated.toISOString();

    if (this._job.crashed()) {

      this._label     = `[downloading...] #${this._job.id()} crashed: ` + this._job.updated.toISOString();
    }

    this.redraw();

    const  resultElement: LineSimJobElement = this;

    const           uuid: string            = this._job.uuid();
    const  projectFolder: string            = this._project.dirname();

    const  resultsFolder: string            = projectFolder + `/results/${uuid}/`;
    const      remoteLog: string            = this._job.details.log;
    const remoteDownload: string            = this._job.details.output;
    const      remoteCmd: string            = this._job.details.simcmd;
    const      remoteKPI: string            = this._job.details.kpi;
    const     remoteKPIX: string            = this._job.details.kpix;
    const   remoteErrors: string            = this._job.details.simerrors;

    const    observables: any[]             = [];
    const tracker: Map<string, {key: string}> = new Map<string, {key: string}>();
    const failed: any[]      = [];

    const        logBase: string            = resultElement._parent.basename() + '.log';
    const         logKey: string            = resultsFolder + logBase;

    const     outputBase: string            = resultElement._parent.basename() + '-output.json';
    const      outputKey: string            = resultsFolder + outputBase;

    const        cmdBase: string            = resultElement._parent.basename() + '-cmd.json';
    const         cmdKey: string            = resultsFolder + cmdBase;

    const        kpiBase: string            = resultElement._parent.basename() + '.kpi';
    const         kpiKey: string            = resultsFolder + kpiBase;

    const        kpixBase: string           = resultElement._parent.basename() + '.kpix';
    const         kpixKey: string           = resultsFolder + kpixBase;

    const     errorsBase: string            = resultElement._parent.basename() + '-errors.txt';
    const      errorsKey: string            = resultsFolder + errorsBase;

    /* build out the observables for everything we're going to download */


    /* the log file ... */

    let lo: Observable<Object> = resultElement._project.services.watcher.linesim.download(
      remoteLog,
      logKey,
      BlobType.File,
      logBase,
      'simulation log',
      this._job.config.endpoint,
      this._job.config.engine,
      this._job.jobId
    ).pipe(
      catchError((err , obs) => {

        let stream: {key: string} = tracker.get('log');

        Logger.debug(`[downloading...] ERROR ${stream.key}`, err);

        let message = "artifact download request Failed!";

        if (isset(err.statusText)) {

          message = err.statusText;
        }

        if (isset(err.error.message)) {

          message = err.error.message;
        }

        failed.push(message);

        Logger.error(`[downloading...] (log) artifact download failed: `, message);

        return of({key: logKey, error: err});
      })
    );

    observables.push(lo);

    tracker.set('log', {key: logKey});

    /* the train movement (output) file ... */

    let oo: Observable<Object>  = resultElement._project.services.watcher.linesim.download(
      remoteDownload,
      outputKey,
      BlobType.Json,
      outputBase,
      'simulation output',
      this._job.config.endpoint,
      this._job.config.engine,
      this._job.jobId
    ).pipe(
      catchError((err , obs) => {

        let stream: {key: string} = tracker.get('output');

        Logger.debug(`[downloading...] ERROR ${stream.key}`, err);

        let message = "artifact download request Failed!";

        if (isset(err.statusText)) {

          message = err.statusText;
        }

        if (isset(err.error.message)) {

          message = err.error.message;
        }

        failed.push(message);

        Logger.error(`[downloading...] (movement) artifact download failed: `, message);

        return of({key: outputKey, error: err});
      })
    );

    observables.push(oo);

    tracker.set('output', {key: outputKey});

    if (!empty(remoteCmd)) {

      this._job.localCmdkey = cmdKey;

      /* the simulation command (parameters) file ... */

      let co: Observable<Object>  = resultElement._project.services.watcher.linesim.download(
        remoteCmd,
        cmdKey,
        BlobType.Json,
        outputBase,
        'simulation command',
        this._job.config.endpoint,
        this._job.config.engine,
        this._job.jobId
      ).pipe(
        catchError((err , obs) => {

          let stream: {key: string} = tracker.get('cmd');

          Logger.debug(`[downloading...] ERROR ${stream.key}`, err);

          let message = "artifact download request Failed!";

          if (isset(err.statusText)) {

            message = err.statusText;
          }

          if (isset(err.error.message)) {

            message = err.error.message;
          }

          failed.push(message);

          Logger.error(`[downloading...] (command) artifact download failed: `, message);

          return of({key: cmdKey, error: err});
        })
      );

      observables.push(co);

      tracker.set('cmd', {key: cmdKey});
    }

    if (!empty(remoteKPI)) {

      this._job.localKPIkey = kpiKey;

      /* the key performance indicators (kpi) file ... */

      let ko: Observable<Object>  = resultElement._project.services.watcher.linesim.download(
        remoteKPI,
        kpiKey,
        BlobType.KPI,
        kpiBase,
        'key performance indicators',
        this._job.config.endpoint,
        this._job.config.engine,
        this._job.jobId
      ).pipe(
        catchError((err , obs) => {

          let stream: {key: string} = tracker.get('kpi');

          Logger.debug(`[downloading...] ERROR ${stream.key}`, err);

          let message = "artifact download request Failed!";

          if (isset(err.statusText)) {

            message = err.statusText;
          }

          if (isset(err.error.message)) {

            message = err.error.message;
          }

          failed.push(message);

          Logger.error(`[downloading...] (kpi) artifact download failed: `, message);

          return of({key: kpiKey, error: err});
        })
      );

      observables.push(ko);

      tracker.set('kpi', {key: kpiKey});
    }

    if (!empty(remoteKPIX)) {

      this._job.localKPIXkey = kpixKey;

      /* the key performance indicators extended (kpix) file ... */

      let xo: Observable<Object>  = resultElement._project.services.watcher.linesim.download(
        remoteKPIX,
        kpixKey,
        BlobType.KPIX,
        kpixBase,
        'key performance indicators (extended)',
        this._job.config.endpoint,
        this._job.config.engine,
        this._job.jobId
      ).pipe(
        catchError((err , obs) => {

          let stream: {key: string} = tracker.get('kpix');

          Logger.debug(`[downloading...] ERROR ${stream.key}`, err);

          let message = "artifact download request Failed!";

          if (isset(err.statusText)) {

            message = err.statusText;
          }

          if (isset(err.error.message)) {

            message = err.error.message;
          }

          failed.push(message);

          Logger.error(`[downloading...] (kpix) artifact download failed: `, message);

          return of({key: kpixKey, error: err});
        })
      );

      observables.push(xo);

      tracker.set('kpix', {key: kpixKey});

    }

    if (!empty(remoteErrors)) {

      this._job.localErrorskey = errorsKey;

      /* the errors file ... */

      let eo: Observable<Object>  = resultElement._project.services.watcher.linesim.download(
        remoteErrors,
        errorsKey,
        BlobType.File,
        outputBase,
        'validation/simulation errors',
        this._job.config.endpoint,
        this._job.config.engine,
        this._job.jobId
      ).pipe(
        catchError((err , obs) => {

          let stream: {key: string} = tracker.get('errors');

          Logger.debug(`[downloading...] ERROR ${stream.key}`, err);

          let message = "artifact download request Failed!";

          if (isset(err.statusText)) {

            message = err.statusText;
          }

          if (isset(err.error.message)) {

            message = err.error.message;
          }

          failed.push(message);

          Logger.error(`[downloading...] (errors) artifact download failed: `, message);

          return of({key: errorsKey, error: err});
        })
      );

      observables.push(eo);

      tracker.set('errors', {key: errorsKey});

    }

    /* fork and join the downloads so they happen in parallel */

    /*
     * Note: forkJoin fails overall if any of its items fail.  This is not good for us,
     * because if the simulator crashes, some of the artifact download requests may fail,
     * since the artifacts don't exist...the simulator crashed!  So, we have to intercept
     * the results as they come back, and provide a fake null result so we can gracefully
     * handle any artifact that is actually missing.
     *
     */

    resultElement._label     = `[0%] #${this._job.id()} downloaded...`;
    resultElement.redraw();

    let N: number = observables.length;
    let n: number = 0;

    forkJoin(observables.map(o => o.pipe(tap((item: any) => {

      /* each tapped item is the blob result (blob meta) from the individual download request */

      /* as the downloads complete, update the element with download progress */

      n++;

      const progress: string = (n/N).toFixed(2);

      const key              = item.key;
      let   msg              = '';

      if (key == logKey) {

        msg = 'log downloaded';

      } else if (key == outputKey) {

        msg = 'movement downloaded';

      } else if (key == kpiKey) {

        msg = 'KPI downloaded';

      } else if (key == kpixKey) {

        msg = 'KPIX downloaded';

      } else if (key == errorsKey) {

        msg = 'errors downloaded';

      } else if (key == cmdKey) {

        msg = 'command downloaded';

      }

      resultElement._label   = `[${progress}] #${this._job.id()} ${msg}...`;

      resultElement.redraw();

      Logger.debug('[result] saved: ', key);

      this.taskUpdate(msg);

    })))).subscribe({

      next: (results): void => {

        Logger.debug('[result] saved blobs: ', results);

        let errors: any[] = [];

        for (let item of results) {

          if (typeof item.error != 'undefined') {

            /* this one failed */

            errors.push(item);

            continue;
          }

          /* results will be a blob metadata object for reach file we did the delegated save for. */

          if (item.key == logKey) {

            Logger.debug('[result] log saved: ', logKey);

            resultElement._job.localLogkey = logKey;

          }

          if (item.key == outputKey) {

            Logger.debug('[result] output saved: ', outputKey);

            resultElement._job.localOutputKey = outputKey;
          }

        }

        if (errors.length > 0) {

          Logger.error('[result] some outputs could not be downloaded: ', errors);

        } else {

          /* wrap it up! */

          this.taskUpdate(`all downloads complete.`);
        }

        resultElement.finalizeResult();
      },

      error: (err): void => {

        resultElement._parent.unBusy();

        Logger.error(`[result] can't download results:`, errorAsText(err));
      }

    });

  }

  public finalizeResult(): void {

    let lsje: LineSimJobElement = this;

    Logger.debug(`[result] finalizing simulation result...`);

    lsje.downloading = false;

    lsje._parent.unBusy();

    lsje.taskUpdate(`finalizing ...`);

    /* how did the cookie crumble? */

    if (lsje._job.crashed()||lsje._job.failed()) {

      /* on failure show the long-running task as failed. */

      let err = lsje._job.lastError;

      if (lsje._job.crash.length > 0) {

        err = lsje._job.crash[0];
      }

      /* if it's not cloud sim, don't use the cloud icon, us the stand-alone icon */

      let icon   = '/assets/images/cloud-run-icon-32.png';

      if ( ! /cloudsim/.test(lsje._job.config.endpoint)) {

        icon = '/assets/images/line-sim-icon-72.png';
      }

      const update = new TaskSimpleSimFailed(lsje.taskId, err, icon);

      Logger.debug(`[LineSimJobElement] progress: ${this._id} `, update);

      lsje._project.services.taskMonitor.progress(update);

    } else if (lsje._job.complete()) {

      /* if it's not cloud sim, don't use the cloud icon, us the stand-alone icon */

      let icon   = '/assets/images/cloud-run-icon-32.png';

      if ( ! /cloudsim/.test(lsje._job.config.endpoint)) {

        icon = '/assets/images/line-sim-icon-72.png';
      }

      /* on success show the long-running task as completed. */

      const update = new TaskSimpleSimComplete(lsje.taskId, `Simulation Completed!`, icon);

      Logger.debug(`[LineSimJobElement] progress: ${lsje._id} `, update);

      lsje._project.services.taskMonitor.progress(update);

      /*
       * on success we should have reaction time details in the train movement output, use that to
       * decorate the job with a reaction time property.
       *
       */

      let prt: number = lsje._job?.details?.passenger_reaction_time ?? 0;
      let frt: number = lsje._job?.details?.freight_reaction_time ?? 0;

      const rt: PropertyElement = ProjectElementFactory.fromObject(
        ProjectElementType.Property,
        {
          project: lsje._project,
          id:      '',
          name:    `P: ${(prt / 60).toFixed(1)} mins, F: ${(frt / 60).toFixed(1)} mins`,
          value:   '',
          data:    new Map(Object.entries({passenger_reaction_time: prt, freight_reaction_time: frt})),
          parent:  lsje,
          publish: true,
          icon:    `reaction-time-32.png`
        }) as PropertyElement;

      /*
       * when using clousim (Cloud Run based pipeline), it automatically outputs
       * the KPI item.  But it may be that we are using one of the stand alone
       * line simulator endpoints...which don't have KPI built in.
       *
       */

      /*
       * per https://smartnetwork.atlassian.net/browse/SN-1751, we no longer visibly
       * add KPI elements, we just use KPIX which has everything that KPI has.
       *
       */

      /*
      if (lsje._job.localKPIkey) {
      */

        /*
         * if the cloud-run based simulation pipeline completes ok, then we have a KPI
         * and it is downloaded as a blob already, so we can go ahead and create a KPI
         * child at this point.
         *
         */

        /*
        const child: KPIElement = ProjectElementFactory.fromObject(
          ProjectElementType.KPI, {
            project: lsje._project,
            id:      '',
            input:   lsje._job.key,
            output:  lsje._job.localOutputKey,
            kpi:     lsje._job.localKPIkey,
            kind:    ProjectElementType.KPI,
            parent:  lsje,
            publish: true
          }) as KPIElement;
        */

        /* make sure the thing we just created is visible */

        /*
         * we're choosing not to expand jobs
         *
       lsje._project.expand([lsje]);
         *
         */

        /*
        child.setStatus('', 'complete');

        lsje.save();
      }
         */

      if (lsje._job.localKPIXkey) {

        /*
         * if the cloud-run based simulation pipeline completes ok, then we have a KPI
         * and it is downloaded as a blob already, so we can go ahead and create a KPI
         * child at this point.
         *
         */

        const child: KPIXElement = ProjectElementFactory.fromObject(
          ProjectElementType.KPIX, {
            project: lsje._project,
            id:      '',
            input:   lsje._job.key,
            output:  lsje._job.localOutputKey,
            kpix:    lsje._job.localKPIXkey,
            kind:    ProjectElementType.KPIX,
            parent:  lsje,
            publish: true
          }) as KPIXElement;

        /* make sure the thing we just created is visible */

        /*
         *  we're choosing not to expand jobs
         *
         lsje._project.expand([lsje]);
         *
         */

        child.setStatus('', 'complete');

        lsje.save();
      }

      /* check if they requested auto-view when the simulation completes... */

      if (lsje._job.config.autoview === true) {

        /* launch a string viewer, as we now have the input and the output */

        Logger.debug(`[result] job #${lsje._job.id()} launching string viewer...`);

        lsje._project.services.ide.launchWindow(new StringLineViewerMetadata(
          '',
          '',
          lsje._job.key,
          lsje._job.localOutputKey
        ));

      } else {

        Logger.debug(`[result] job #${lsje._job.id()} auto-launch disabled.`);
      }
    }

    /* trigger a redraw */

    lsje._label   = `#${lsje._job.id()} ${SimulationJob.statusString(lsje._job.status)}: ` + lsje._job.updated.toISOString();

    if (lsje._job.crashed()) {

      lsje._label = `#${lsje._job.id()} crashed: ` + lsje._job.updated.toISOString();
    }

    /* we're choosing to keep job details collapsed */

    /*
     * we have to wait for the redraw and save; the collapse request is published, and
     * can't be processed until JavaScript has control again.
     *
     */

    setTimeout((): void => {

      /* trigger a save */

      Logger.debug(`[result] saving project, after new job completed...`);

      lsje._expanded = false;

      lsje._project.collapse([lsje, ...lsje.children()]);

      lsje.save();

    }, 600);

  }

  /*
   * when blobs get moved/copied in bulk, project elements have to allow being told where
   * their blobs have been moved to.
   *
   */
  public adjusted(searchKey: string, replaceKey: string) {

    if (this._job !== null) {

      if (this._job.localLogkey === searchKey) {

        this._job.localLogkey = replaceKey;
      }

      if (this._job.localOutputKey === searchKey) {

        this._job.localOutputKey = replaceKey;
      }
    }

    super.adjusted(searchKey, replaceKey);
  }

  /** fetch any secondary blobs for this element */
  public secondaryBlobs(): string[] {

    if (this._job === null) {

      return [];
    }

    /* sim results have a local copy of the simulation job's log file and the output */

    return [
      this._job.localLogkey,
      this._job.localOutputKey
    ];
  }

  public hasUserLog(): boolean {

    /*
     * if the 'extra' section in the job details has user log info, it will look like:
     *
     *   {
     *      userlog: "mongodb://mongo:mongo@mongodb:27017/linesim"
     *      userlog_location: {
     *        collection: "userlog-607f0919bcba0879ad6eb122",
     *        url: "mongodb://mongo:mongo@mongodb:27017/linesim"
     *      }
     *   }
     *
     */


    /*
     * sometimes when one right clicks (i.e. on a fake job) there will never be user logging and that in between
     * “extra” property will never be there (i.e. - null).  If it's not there, assume userlog is false instead of
     * trying to test a property that isn’t going to be there.
     *
     */

    if (!this._job.extra) {

        return false;
    }

    let userlog: string = ((typeof this._job.extra.userlog != 'undefined') && (this._job.extra.userlog != null))
      ? this._job.extra.userlog : '';

    Logger.debug(`[result] result '${this._job.id()}' has user log: `, this._job.extra.userlog_location);

    return userlog !== '';
  }

  /** test if the output has been downloaded yet */
  public hasOutput(): boolean {

    return !empty(this._job.localOutputKey);
  }

  /** test if the log has been downloaded yet */
  public hasLog(): boolean {

    return !empty(this._job.localLogkey);
  }

  /**
   * subclasses must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    let crashed: boolean = false;

    if (this._job) {

      crashed = this._job.crashed();
    }


    if (crashed) {

      actionSet.viewcrash = {
        name: 'View Crash',
        icon: 'fas fa-bomb',
        disabled: !this.hasOutput()
      };
    }

    if (!crashed) {

      actionSet.viewstringline = {
        name: 'String Line View',
        icon: 'fas fa-train',
        disabled: !this.hasOutput()
      };
    }

    let canUserLog: boolean = this.hasUserLog();

    if (!crashed) {

      actionSet.userlog = {
        name: 'UserLog Focus Here',
        icon: 'fas fa-sitemap',
        disabled: !canUserLog
      };

      actionSet.userlogplus = {
        name: 'UserLog Compare',
        icon: 'fas fa-sitemap',
        disabled: !canUserLog
      };

      actionSet.userlognew = {
        name: 'UserLog in New Tab',
        icon: 'fas fa-sitemap',
        disabled: !canUserLog
      };
    }

    actionSet.downloadlog = {
      name: 'Log (Download)',
      icon: 'fas fa-file-download',
      disabled:  !this.hasLog()
    };

    actionSet.editlog = {
      name: 'Log View',
      icon: 'fas fa-edit',
      disabled: !this.hasLog()
    };

    actionSet.downloadoutput = {
      name: 'Output (Download)',
      icon: 'fas fa-file-download',
      disabled: !this.hasOutput()
    };

    actionSet.viewoutput = {
      name: 'Output View',
      icon: 'fas fa-keyboard',
      disabled: !this.hasOutput()
    };

    actionSet.viewinbrowser = {
      name: 'Folder Of',
      icon: 'fas fa-list',
      disabled: false
    };

    if (!crashed) {
      actionSet.generatekpi = {
        name: 'Generate KPI',
        icon: 'far fa-chart-bar',
        disabled: !this.hasOutput() || (this.children().length > 0) || this.isBusy()
      };

      actionSet.generatekpitable = {
        name: 'Generate KPI Table',
        icon: 'far fa-chart-bar',
        disabled: this.isBusy()
      };
    }

    actionSet.renameresult = {
      name: 'Rename Result',
      icon: 'fas fa-tag',
      disabled: (this._job === null) || this._job.active() ||  this.isBusy()
    };

    actionSet.deleteresult = {
      name: 'Delete Result',
      icon: 'fas fa-trash',
      disabled:  this.isBusy()
    };

    return actionSet;
  }

  /** test if this element accepts the given action */
  public acceptsAction(key: string): boolean {

    return !!this.actionKeys.find(x => x === key);
  }

  /** append these elements properties to the holder */
  public properties(properties?: PropertiesHolder): PropertiesHolder {

    if (!properties) {

      properties = new PropertiesHolder();

    }

    /* show as much detail as we have */

    if (this._job) {

      /* basics */

      properties.set('Job ID',                  this._job.jobId.toString());
      properties.set('Created',                 this._job.created.toISOString());
      properties.set('Updated',                 this._job.updated.toISOString());
      properties.set('Status',                  SimulationJob.statusString(this._job.status));
      properties.set('Input',                   this._job.key);
      properties.set('Output',                  this._job.localOutputKey);
      properties.set('Log',                     this._job.localLogkey);
      properties.set('Command',                 this._job.localCmdkey);
      properties.set('KPI',                     this._job.localKPIkey);
      properties.set('Errors',                  this._job.localErrorskey);
      properties.set('Endpoint',                this._job.config.endpoint);
      properties.set('Total Time',              this._job.details.totaltime.toString());
      properties.set('Run Time',                this._job.details.runtime.toString());
      properties.set('Scenario',                this._job.details.name);
      properties.set('Passenger Reaction Time', this._job.details.passenger_reaction_time.toString());
      properties.set('Freight Reaction Time',   this._job.details.freight_reaction_time.toString());
      properties.set('Simulated',               `${this._job?.details.compile_date} ${this._job?.details.compile_time}`);
      properties.set('Version',                 this._job?.details.version);
      properties.set('Client ID',               this._job?.details.client_id.toString());

      /*
       * get the response lines, and if we don't have an error set, and we're in an error state...
       * fall back on the last response line as the error.
       *
       */

      try {

        const response: string[] = JSON.parse(this._job.response);

        if (this._job.failed()) {

          if (this._job.lastError == '') {

            this._job.lastError = response[response.length - 1];
          }
        }

      } catch (error) {

        /* try to just parse it as string */

        let out: string[] = [];

        if (typeof this._job.response == 'string') {

          out = this._job.response.split(/\r?\n/);
        }

        if (this._job.failed()) {

          if (this._job.lastError == '') {

            this._job.lastError = out[out.length - 1];
          }
        }

      }

      /* show the last error, hopefully empty because it was a success */

      properties.set('Error',      this._job.lastError);

    }

    /* add in the usual properties */

    return super.properties(properties);
  }

  /** generate the KPI child for this line sim job */
  public generateKPI(): void {

    const lse: LineSimJobElement = this;
    const           uuid: string = lse._job.uuid();
    const  projectFolder: string = lse._project.dirname();
    const      kpiFolder: string = projectFolder + `/kpi/${uuid}/`;
    const         kpiKey: string = kpiFolder + lse._parent.basename() + '.kpi';

    Logger.debug('[result] generating  KPI:', lse._parent._blobKey, lse._job.localOutputKey, kpiKey);

    /*
     * we create the KPI element immediately, even though it isn't usable...so that we can give
     * the user some visual feedback that it is pending...
     *
     */

    const child: KPIElement = ProjectElementFactory.fromObject(
      ProjectElementType.KPI, {
        project: lse._project,
        id:      '',
        input:   '',
        output:  '',
        kpi:     '',
        kind:    ProjectElementType.KPI,
        parent:  lse,
        publish: true
      }) as KPIElement;

    child.setStatus('Pending...', 'pending');

    /* make sure the thing we just created is visible */

    lse._project.expand([lse]);

    lse.save();

    /* generate the KPI and save it to our chose KPI blob location... */

    lse._project.services.kpi.kpi(lse._parent._blobKey, lse._job.localOutputKey, kpiKey)
      .pipe(first())
      .subscribe({

        next: (triplet: any): void => {

          Logger.debug(`[result] saving KPI:`, triplet);

          /* the KPI generation succeeded, save the co-ordinates ... */

          child._inputKey  = triplet.input;
          child._outputKey = triplet.output;
          child._kpiKey    = triplet.kpi;

          /* update the display to success */

          child.setStatus('', 'complete');

          /* trigger a project save */

          lse.save();
        },

        error: (err: any): void => {

          /* update the display to failed */

          let errMsg: string = errorAsText(err);

          child.setStatus(errMsg, 'failed');

          Logger.error(`[result] can't generate kpi blob: `, err);

          /* trigger a project save */

          lse.save();
        }

      });

  }

  // noinspection JSUnusedLocalSymbols
  /** helper to delete any cached artifacts from the remote simulation */
  public deleteResult(selection: ProjectElement[]) {

    if (this.pendingKeys.length > 0) {

      /* busy right now */

      return ;
    }

    /*
     * the cached results are internal to a "result", we just need to batch them up,
     * and remove them all with a fork/join.  In our case there are just two artifacts,
     * the log and the output.
     *
     */

    this.pendingKeys = [];

    this.pendingKeys.push([this._job.localLogkey,    'latest']);
    this.pendingKeys.push([this._job.localOutputKey, 'latest']);

    /* build out the blob deletion observables ... */

    Logger.debug('[result] deleting blobs: ', this.pendingKeys);

    /* create the observables for copying blobs... */

    const observables: any[] = [];

    for (const f of this.pendingKeys) {

      /* as we copy we are also forcing the type to be whatever the user selected as the import type */

      observables.push( this._project.services.blobber.remove(f[0], f[1]).pipe(first()) );
    }

    /* now fork & join to do them all in parallel */

    forkJoin(observables).subscribe(
      (results) => {

        /* everything got deleted ok, add the children in the project and save the latest project */

        Logger.debug('[result] blobs deleted: ', results);

        /* make sure the project is saved */

        this.pendingKeys = [];

        /* stop watching the job */

        this._project.services.watcher.unMonitor(this._job);

        /* delete self */

        this.deleteElements([this]);

      },
      (error) => {

        Logger.error('[result] problems deleting blobs: ', error);

      });

  }

  /** subclasses must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    if ( !this.actionKeys.find(x => x === key) ) {

      /* let ProjectElement handle it */

      this._parent.action(key, selection);

      return ;
    }

    switch (key) {

      case 'viewinbrowser': {

        this._project.services.ide.launchWindow(FileBrowserMetadata.build(this._job.localOutputKey, this._job.localOutputKey));

        break;
      }

      case 'generatekpitable': {

        this._project.generateKPITable(this);

      }
      break;

      case 'generatekpi': {

        this.generateKPI();
      }
      break;

      case 'viewstringline': {

        this._project.services.ide.launchWindow(
          new StringLineViewerMetadata(
            '',
            '',
            this._parent._blobKey,
            this._job.localOutputKey
          )
        );

        break;
      }

      case 'downloadlog': {

        this.dowloadBlob(BlobType.File, this._job.localLogkey, this._parent.basename() + '.log');
        break;
      }

      case 'viewcrash': {

        this._project.services.ide.launchWindow(
          new TextEditorMetadata(
            '',
            '',
            this._job.crash.join('\n'),
            'rawtext'
          )
        );

        break;
      }

      case 'editlog': {

        this._project.services.ide.launchWindow(
          new TextEditorMetadata(
            '',
            '',
            this._job.localLogkey
          )
        );

        break;
      }

      case 'downloadoutput': {

        let rtf: string = `RT_F${(this._job.details.freight_reaction_time ).toFixed(0)}_P${(this._job.details.passenger_reaction_time).toFixed(0)}_`

        this.dowloadBlob(BlobType.Json, this._job.localOutputKey, 'latest', this._parent.basename() + `-output-${rtf}.json`);

        break;
      }

      case 'viewoutput': {

        this._project.services.ide.launchWindow(
          new JSONBrowserMetadata(
            '',
            '',
            this._job.localOutputKey
          )
        );

        break;
      }

      case 'renameresult': {

        /*
         * there is nothing below a sim result hat would need path adjusting, and labeling,
         * we have to keep the job id; both in the project view and the jobs view.  So really
         * this is about having an "alternate" label after the #ID.
         *
         */

        const lsr: LineSimJobElement = this;

        lsr._project.services.modals.open(
          'rename-element-modal',
          (name, resolve, reject) => {

            name = `#${this._job.id()} ${name.trim()}`;

            Logger.debug('[LineSimJobElement] validating: ', name);

            /* make sure we aren't name clashing ...  */

            const peers: ProjectElement[] = lsr._parent._children;

            for (const peer of peers) {

              if (peer._label === name) {

                reject('Result of that name already exists.');

                return ;
              }

            }

            resolve(name);

          },
          (name) => {

            /* new label was accepted */

            Logger.debug('[LineSimJobElement] renaming: ', name);

            lsr._label = `${name.trim()}`;

            lsr.redraw();

            lsr.save();
          },
          {
            title: 'Rename Result',
            placeholder: this._label
          });

      }
      break;

      case 'deleteresult': {

        Logger.debug('[BlobElement] deleting');

        this.deleteElements(selection);

        break;
      }

      case 'userlog': {

        /* get the user log details */

        let location:      any = this._job.extra.userlog_location;
        let dsn:        string = location.url;
        let collection: string = location.collection;

        /* launch */

        const inputTag: string = `# ${this._job.id()} - ${this._parent._label}`;

        this._project.services.ide.refocus(
          new UserLogViewerMetadata(
            '',
            this._label,
            dsn,
            collection,
            inputTag
          ),
          false
        );

        break;
      }

      case 'userlogplus': {

        /* get the user log details */

        let location:      any = this._job.extra.userlog_location;
        let dsn:        string = location.url;
        let collection: string = location.collection;

        /* launch */

        const inputTag: string = `# ${this._job.id()} - ${this._parent._label}`;

        this._project.services.ide.refocus(
          new UserLogViewerMetadata(
            '',
            this._label,
            dsn,
            collection,
            inputTag
          ),
          true
        );

        break;
      }

      case 'userlognew': {

        /* always launches a completely new user logging panel */

        /* get the user log details */

        let location:      any = this._job.extra.userlog_location;
        let dsn:        string = location.url;
        let collection: string = location.collection;

        /* launch */

        const inputTag: string = `# ${this._job.id()} - ${this._parent._label}`;

        this._project.services.ide.launchWindow(
          new UserLogViewerMetadata(
            '',
            this._label,
            dsn,
            collection,
            inputTag
          )
        );

        break;
      }

    }
  }
}
